"use client";

import { useCallback } from "react";
import { useVideoAutoplay } from "@/components/providers/video-autoplay-provider";
import { PosterVideo } from "@/components/ui/poster-video";

type FeedVideoProps = {
  /**
   * Stable identity for the autoplay/audio registry (e.g.
   * `post-12-media-abc`). The `MediaSoundButton` for the same video must pass
   * the identical id.
   */
  audioId: string;
  src: string;
  mimeType?: string;
  posterUrl?: string | null;
  blurHash?: string | null;
  /** The media frame this fills; the poster covers it until a frame paints. */
  className?: string;
  /** `object-fit` and other classes for the `<video>` itself. */
  videoClassName?: string;
  /** Decorative/embedded contexts: autoplay in view but never own audio. */
  forceMuted?: boolean;
  /** `sizes` for the poster cover; defaults to a full-width feed frame. */
  posterSizes?: string;
  /** `object-fit` for the poster cover; matches `videoClassName` for contain. */
  posterClassName?: string;
  /** Accessible name for contexts where the video is the only content. */
  ariaLabel?: string;
  /**
   * False keeps the player mounted (and paused) but out of the autoplay
   * registry. A media strip uses it so only the card it considers current can
   * claim the page's playing slot — the provider's "most visible" rule alone
   * would start a card that is merely peeking in.
   */
  playbackEnabled?: boolean;
};

/**
 * Instagram-style feed video: registers with the global VideoAutoplayProvider
 * (same registry as /hiives cards) so it autoplays when ~60% visible and
 * pauses off-screen. Mobile follows the user's global sound preference;
 * desktop retains a single manually selected audio owner.
 */
export function FeedVideo({
  audioId,
  src,
  mimeType,
  posterUrl,
  blurHash,
  className,
  videoClassName,
  forceMuted = false,
  posterSizes = "(max-width: 768px) 100vw, 600px",
  posterClassName,
  ariaLabel,
  playbackEnabled = true,
}: FeedVideoProps) {
  const { isMuted, register } = useVideoAutoplay();
  // Only this player's ref re-runs when `playbackEnabled` flips: detach
  // unregisters (and pauses), attach registers again — the element survives.
  const setVideoRef = useCallback(
    (element: HTMLVideoElement | null) =>
      register(audioId, playbackEnabled ? element : null),
    [audioId, playbackEnabled, register],
  );

  return (
    <PosterVideo
      ref={setVideoRef}
      src={src}
      mimeType={mimeType || "video/mp4"}
      posterUrl={posterUrl}
      blurHash={blurHash}
      posterSizes={posterSizes}
      posterClassName={posterClassName}
      aria-label={ariaLabel}
      className={className}
      videoClassName={videoClassName}
      playsInline
      muted={forceMuted || isMuted(audioId)}
      loop
      preload="metadata"
      data-force-muted={forceMuted ? "true" : undefined}
    />
  );
}
